import { last } from 'lodash';

import { createBasePipeline } from '@/core/pipeline';
import { getCellByPipelineIdOrThrow } from '@/core/cell/cells';

import { getJoinKeyToSemanticModel, getModel } from '../model/utils';
import { getFinalState, PipelineStateContext } from '../pipeline/state';
import { flattenPipeline } from '../pipeline/utils';
import type {
  BasePipeline,
  Exploration,
  Fields,
  FunnelOperation,
  FunnelStep,
  Model,
  Pipeline,
  PipelineState,
} from '../types';

export const getStepName = (
  step: FunnelStep,
  models: Model[],
  exploration: Exploration,
): string => {
  const { baseModelId } = getFlattenedStepPipeline(step, exploration);
  const model = getModel(models, baseModelId);
  const modelName = model?.name ?? `❗ Missing model: ${baseModelId}`;
  if ('parentId' in step.pipeline) {
    const cell = getCellByPipelineIdOrThrow(exploration.view.cells, step.pipeline.parentId);
    const cellTitle = 'title' in cell ? cell.title : undefined;
    return cellTitle ?? modelName;
  }
  return modelName;
};

export const getFlattenedStepPipeline = (
  step: FunnelStep,
  exploration: Exploration,
): BasePipeline => flattenPipeline(step.pipeline, exploration);

export const getStepPipelineState = (step: FunnelStep, ctx: PipelineStateContext) => {
  const { baseModelId, operations } = getFlattenedStepPipeline(step, ctx.exploration);
  const stepOperations = 'pipeline' in step ? step.pipeline.operations : [];
  const parentOperations = operations.slice(0, operations.length - stepOperations.length);
  const pipelineState = getFinalState(
    createBasePipeline({ baseModelId, operations: parentOperations }),
    ctx,
  );
  return pipelineState;
};

/**
 * Return the final pipeline states of the funnel steps
 */
export const getFunnelPipelineStates = (steps: FunnelStep[], ctx: PipelineStateContext) =>
  steps.filter((step) => !isEmptyStep(step)).map((step) => getStepPipelineState(step, ctx));

export const getSharedFields = (pipelineStates: PipelineState[]) => {
  if (pipelineStates.length === 0) {
    return [];
  }

  const fields = pipelineStates.map((state) => state.fields);
  if (fields.length === 1) {
    return fields[0];
  }

  const [firstFields, ...restFields] = fields;

  return firstFields.reduce<Fields>((acc, field) => {
    if (restFields.every((f) => Boolean(f.find(({ key }) => key === field.key)))) {
      return [...acc, field];
    }

    return acc;
  }, []);
};

const getFirstIdField = (fields: Fields) =>
  fields.find((field) => field.pk === true || field.key.endsWith('_id'));

export const getDefaultCountingFieldKey = (fields: Fields, model: Model, models: Model[]) => {
  return (
    fields.find((field) => field.key === getJoinKeyToSemanticModel(model, models, 'User'))?.key ??
    fields.find((field) => field.key === getJoinKeyToSemanticModel(model, models, 'Account'))
      ?.key ??
    getFirstIdField(fields)?.key ??
    fields[0].key
  );
};

export const getDefaultSortFieldKey = (fields: Fields, model: Model) =>
  fields.find((field) => field.key === model.semantics?.properties?.createdAt)?.key ??
  fields.find((field) => field.type === 'Date')?.key ??
  fields[0].key;

export const getEmptyFunnelStep = (): FunnelStep => ({
  sortKey: '',
  fields: [],
  pipeline: createBasePipeline({
    baseModelId: '',
    operations: [],
  }),
});

export const isEmptyStep = (step: FunnelStep) =>
  !('baseModelId' in step.pipeline && step.pipeline.baseModelId !== '') &&
  !('parentId' in step.pipeline && step.pipeline.parentId !== '');

export const getFunnelOperation = (pipeline: Pipeline) => {
  const operation = last(pipeline.operations);

  if (operation?.operation !== 'funnel') {
    throw new Error('Failed to find funnel operation');
  }

  return removeEmptyFunnelSteps(operation);
};

/**
 * Used to remove empty steps before validation so we don't constantly toggle between
 * funnels and errors while the user is building the funnel.
 */
export const removeEmptyFunnelSteps = (operation: FunnelOperation) => {
  return {
    ...operation,
    parameters: {
      ...operation.parameters,
      steps: operation.parameters.steps.filter((step) => !isEmptyStep(step)),
    },
  };
};
